import React from 'react';
import { Check, Loader2, Circle, XCircle } from 'lucide-react';
import type { PlanStep } from '@/components/copilot/mcp/chatTypes';

interface PlanStepperProps {
  steps: PlanStep[];
}

const StepIcon: React.FC<{ status: PlanStep['status'] }> = ({ status }) => {
  switch (status) {
    case 'completed':
      return (
        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
          <Check className="w-3 h-3 text-green-600" />
        </div>
      );
    case 'running':
      return (
        <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
          <Loader2 className="w-3 h-3 text-blue-600 animate-spin" />
        </div>
      );
    case 'failed':
      return (
        <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
          <XCircle className="w-3 h-3 text-red-500" />
        </div>
      );
    default:
      return (
        <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
          <Circle className="w-3 h-3 text-gray-400" />
        </div>
      );
  }
};

export const PlanStepper: React.FC<PlanStepperProps> = ({ steps }) => {
  if (!steps || steps.length === 0) return null;

  return (
    <div className="mb-3 space-y-0">
      {steps.map((step, idx) => (
        <div key={idx} className="flex items-start gap-2 relative">
          {/* Vertical connector line */}
          {idx < steps.length - 1 && (
            <div className="absolute left-[9px] top-5 w-px h-[calc(100%)] bg-gray-200" />
          )}
          <StepIcon status={step.status} />
          <div className="flex-1 pb-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-500">Step {idx + 1}</span>
              {step.tool_name && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono">
                  {step.tool_name}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-700 mt-0.5">{step.description || step.name}</p>
          </div>
        </div>
      ))}
    </div>
  );
};
